import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  IconButton,
  Divider,
  CircularProgress,
  Drawer,
  Button,
  useTheme,
  Avatar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BugReportIcon from "@mui/icons-material/BugReport";
import { tokens } from "../../theme";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import { Image } from "@mui/icons-material";

import ViewModuleIcon from "@mui/icons-material/ViewModule";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PublicIcon from "@mui/icons-material/Public";
import DevicesIcon from "@mui/icons-material/Devices";
import AspectRatioIcon from "@mui/icons-material/AspectRatio";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeveloperWorkload from "./DeveloperWorkLoad";
import AssignmentPanel from "./Assignment";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import apiService from "../../service/Api";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../constants/permissions";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdminBugAssignments,
  fetchBugAdminDashboard,
} from "../../store/slice/bug-fix/bugSlice";
import { BugPriority, BugReport } from "../../store/slice/bug-fix/types";
import { AppDispatch, RootState } from "../../store/Index";
import { toast } from "react-toastify";

/* =====================
   Types
===================== */

interface StatCard {
  label: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  subTitleColor: string;
}

const status_choices = {
  new: "New",
  assigned: "Assigned",
  in_progress: "In Progress",
  resolved: "Resolved",
  blocked: "Blocked",
};

const bugtype_choices = {
  ui_issue: "Something doesn’t look right",
  feature_not_working: "A feature isn’t working",
  performance: "The system feels slow",
  access_issue: "I can’t access something",
  other: "Something else",
};

const occurance_choices = {
  every_time: "Every time",
  sometimes: "Sometimes",
  once: "Just once",
};

const impact_choices = {
  minor: "Minor inconvenience",
  slowed: "Slowed me down",
  blocked: "Blocked my work",
};

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const parts = new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(date);

  const get = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "";

  return `${get("day")} ${get("month")} ${get("year")} ${get("hour")}:${get("minute")} ${get("dayPeriod")}`;
};

const isOverdue = (bug: BugReport) =>
  Boolean(
    bug.due_date &&
    bug.status !== "resolved" &&
    new Date(bug.due_date) < new Date(),
  );

/* =====================
   Component
===================== */

export default function BugsAdminDashboard() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [selectedBug, setSelectedBug] = React.useState<BugReport | null>(null);

  const { hasPermission } = usePermissions();
  const labelColor = "rgba(148, 163, 184, 1)";
  const dispatch = useDispatch<AppDispatch>();
  const [searchQuery, setSearchQuery] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [priorityFilter, setPriorityFilter] = React.useState("all");
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const [assigning, setAssigning] = React.useState(false);
  const [initialLoading, setInitialLoading] = React.useState(true);
  const [, setIsRecent] = React.useState<boolean>(false);
  const [isImageOpen, setIsImageOpen] = React.useState(false);
  const { assignments, adminDashboard } = useSelector(
    (state: RootState) => state.bugs,
  );

  const recentBugs = React.useMemo(() => {
    if (!adminDashboard?.recent_bugs) return [];
    return adminDashboard.recent_bugs;
  }, [adminDashboard]);

  const stats: StatCard[] = React.useMemo(() => {
    if (!adminDashboard) {
      return [
        {
          label: "Open bugs",
          value: 0,
          subtitle: "+0 Since yesterday",
          icon: (
            <BugReportIcon sx={{ color: "#007AFF", height: 24, width: 24 }} />
          ),
          color: "rgba(0, 122, 255, 0.15)",
          subTitleColor: "#8F8F8F",
        },
        {
          label: "Critical",
          value: 0,
          subtitle: "Needs immediate attention",
          icon: (
            <WhatshotIcon sx={{ color: "#FF3B30", height: 24, width: 24 }} />
          ),
          color: "rgba(255, 59, 48,0.15)",
          subTitleColor: "#FF3B30",
        },
        {
          label: "Avg Fix time",
          value: "0d",
          subtitle: "—",
          icon: (
            <AccessTimeIcon
              sx={{ color: "rgba(175, 82, 222, 1)", height: 24, width: 24 }}
            />
          ),
          color: "rgba(175, 82, 222, 0.15) ",
          subTitleColor: "#0D9488",
        },
        {
          label: "Resolved",
          value: 0,
          subtitle: "Last 7 days",
          icon: (
            <CheckCircleIcon
              sx={{ color: "rgba(13, 148, 136,1)", height: 24, width: 24 }}
            />
          ),
          color: "rgba(13, 148, 136,0.15) ",
          subTitleColor: "#8F8F8F",
        },
      ];
    }

    return [
      {
        label: "Open bugs",
        value: adminDashboard.stats.open_bugs,
        subtitle: "+2 Since yesterday",
        icon: (
          <BugReportIcon sx={{ color: "#007AFF", height: 24, width: 24 }} />
        ),
        color: "rgba(0, 122, 255, 0.15)",
        subTitleColor: "#8F8F8F",
      },
      {
        label: "Critical",
        value: adminDashboard.stats.critical_bugs,
        subtitle: "Needs immediate attention",
        icon: <WhatshotIcon sx={{ color: "#FF3B30", height: 24, width: 24 }} />,
        color: "rgba(255, 59, 48,0.15)",
        subTitleColor: "#FF3B30",
      },
      {
        label: "Avg Fix time",
        value: `${adminDashboard.stats.avg_fix_time_days}d`,
        subtitle:
          adminDashboard.stats.avg_fix_trend === "improving"
            ? "↓ Improving"
            : adminDashboard.stats.avg_fix_trend === "worsening"
              ? "↑ Worsening"
              : "— Stable",
        icon: (
          <AccessTimeIcon
            sx={{ color: "rgba(175, 82, 222, 1)", height: 24, width: 24 }}
          />
        ),
        color: "rgba(175, 82, 222, 0.15) ",
        subTitleColor:
          adminDashboard.stats.avg_fix_trend === "improving"
            ? "#0D9488"
            : adminDashboard.stats.avg_fix_trend === "worsening"
              ? "#FF3B30"
              : "#8F8F8F",
      },
      {
        label: "Resolved",
        value: adminDashboard.stats.resolved_last_7_days,
        subtitle: "Last 7 days",
        icon: (
          <CheckCircleIcon
            sx={{ color: "rgba(13, 148, 136,1)", height: 24, width: 24 }}
          />
        ),
        color: "rgba(13, 148, 136,0.15) ",
        subTitleColor: "#8F8F8F",
      },
    ];
  }, [adminDashboard]);

  const filteredAssignments = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return assignments.filter((assignment) => {
      if (statusFilter !== "all" && assignment.bug.status !== statusFilter) {
        return false;
      }
      if (priorityFilter !== "all" && assignment.priority !== priorityFilter) {
        return false;
      }
      if (!query) return true;

      const haystack = [
        assignment.bug.description,
        assignment.bug.problem,
        assignment.bug.id,
        assignment.bug.module_name,
        bugtype_choices[assignment.bug.bugtype],
        assignment.bug.status,
        assignment.priority,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [assignments, searchQuery, statusFilter, priorityFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredAssignments.length / rowsPerPage),
  );
  const currentPage = Math.min(page, totalPages);
  const pagedAssignments = React.useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredAssignments.slice(start, start + rowsPerPage);
  }, [filteredAssignments, currentPage, rowsPerPage]);

  React.useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter, priorityFilter, rowsPerPage]);

  React.useEffect(() => {
    if (page !== currentPage) {
      setPage(currentPage);
    }
  }, [page, currentPage]);

  // React.useEffect(() => {
  //     if (selectedBug) {
  //         setStatus(selectedBug.status);
  //     }
  // }, [selectedBug]);

  const handleAssign = async (payload: {
    bug: BugReport;
    developerId: number;
    priority: BugPriority;
    dueDate?: string;
  }) => {
    try {
      setAssigning(true);
      const idParts = payload.bug.id.split("-");
      const bugPk = Number(idParts[idParts.length - 1]);

      await apiService.post("/api/bug-report/bug-assignments/", {
        bug_id: bugPk,
        assigned_to_id: payload.developerId,
        bug: payload.bug,
        priority: payload.priority,
      });

      if (payload.dueDate) {
        try {
          await apiService.patch(`/api/bug-report/bugs/${bugPk}/`, {
            due_date: payload.dueDate,
          });
        } catch (dueDateError: unknown) {
          console.log(dueDateError);
          toast.error(
            "Developer assigned, but the due date couldn't be saved.",
          );
        }
      }

      await dispatch(fetchBugAdminDashboard());
      await dispatch(fetchAdminBugAssignments());
      toast.success("Developer assigned successfully!");
      setSelectedBug(null);
    } catch (error: unknown) {
      console.log(error);
      toast.error("Failed to assign developer. Please try again.");
    } finally {
      setAssigning(false);
    }
  };

  React.useEffect(() => {
    const load = async () => {
      await dispatch(fetchBugAdminDashboard());
      await dispatch(fetchAdminBugAssignments());
      setInitialLoading(false);
    };
    load();
  }, [dispatch]);

  const InfoItem = ({
    icon,
    label,
    value,
  }: {
    icon: React.ReactNode;
    label: string;
    value?: string;
  }) => (
    <Stack direction="row" spacing={1.5} flex={1} alignItems="flex-start">
      {icon}
      <Box>
        <Typography color={labelColor} variant="caption">
          {label}
        </Typography>
        <Typography variant="body2">{value || "-"}</Typography>
      </Box>
    </Stack>
  );
  if (initialLoading) {
    return (
      <Box
        p={3}
        bgcolor="#f6f8fc"
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <Box>
      {/* =====================
         TOP STATS
      ===================== */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3}>
        {stats.map((stat) => (
          <Card
            key={stat.label}
            sx={{
              flex: 1,
              borderRadius: "16px",
              borderColor: "#E3E3E3",
              boxShadow: "none",
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    bgcolor: stat.color,
                    p: 1.5,
                    borderRadius: "100%",
                    height: 50,
                    width: 50,
                  }}
                >
                  {stat.icon}
                </Box>
                <Box>
                  <Typography variant="h6">{stat.value}</Typography>
                  <Typography variant="body2">{stat.label}</Typography>
                  <Typography variant="caption" color={stat.subTitleColor}>
                    {stat.subtitle}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Dev workload */}
      <DeveloperWorkload developers={adminDashboard?.developer_workload} />

      {/* =====================
                     RECENT + WORKLOAD
                  ===================== */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} my={3}>
        <Card sx={{ flex: 3, borderRadius: "16px", boxShadow: "none" }}>
          <CardContent>
            <Typography fontWeight={600} mb={2}>
              Recent Bug Reports
            </Typography>

            <Stack spacing={1.5} sx={{ maxHeight: 300, overflowY: "auto" }}>
              {recentBugs.length === 0 ? (
                <Box
                  p={3}
                  borderRadius={2}
                  bgcolor="#f3f6f9"
                  textAlign="center"
                  sx={{
                    flex: 1,
                    alignItems: "center",
                    justifyContent: "center",
                    height: 250,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No recent bugs.
                  </Typography>
                </Box>
              ) : (
                recentBugs.map((bug: BugReport, index) => {
                  const impactColor =
                    bug.impact === "blocked"
                      ? "#f44336"
                      : bug.impact === "slowed"
                        ? "#ff9500"
                        : "rgba(148, 163, 184, 1)";

                  return (
                    <Box
                      key={index}
                      onClick={() => {
                        setSelectedBug(bug);
                        setIsRecent(true);
                      }}
                      sx={{
                        borderRadius: "12px",
                        border: "1px solid rgba(226, 232, 240, 1)",
                        borderLeft: `3px solid ${impactColor}`,
                        bgcolor: colors.primary[900],
                        p: 2,
                        cursor: "pointer",
                        transition: "border-color 0.15s ease",
                        "&:hover": { borderColor: "rgba(160, 174, 192, 1)" },
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={2}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {bug.id}
                          </Typography>
                          <Typography fontWeight={600} noWrap>
                            {bug.description}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ display: "block" }}
                          >
                            {bug.problem}
                          </Typography>
                        </Box>
                        {(bug.impact === "blocked" ||
                          bug.impact === "slowed") && (
                          <WarningIcon
                            sx={{
                              color: impactColor,
                              height: 20,
                              width: 20,
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        flexWrap="wrap"
                        rowGap={1}
                        mt={1.5}
                        divider={
                          <Box
                            sx={{
                              width: "1px",
                              height: "14px",
                              bgcolor: "rgba(226, 232, 240, 1)",
                            }}
                          />
                        }
                      >
                        <Chip
                          label={bug.module_name}
                          size="small"
                          sx={{
                            color: "#fff",
                            backgroundColor: "#A2845E",
                            height: 22,
                          }}
                        />
                        <Chip
                          label={status_choices[bug.status]}
                          size="small"
                          sx={{
                            color: "rgba(255, 149, 0, 1)",
                            backgroundColor: "rgba(255, 149, 0, 0.05)",
                            border: "1px solid rgba(255, 149, 0, 1)",
                            height: 22,
                          }}
                        />
                        <Typography variant="caption" color="error">
                          {formatDateTime(bug.created_at)}
                        </Typography>
                      </Stack>
                    </Box>
                  );
                })
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* =====================
         BUG LIST
      ===================== */}
      <Card sx={{ boxShadow: "none", borderRadius: "16px" }}>
        <CardContent>
          <Typography fontWeight={600} mb={2}>
            Bug List
          </Typography>

          <Stack direction="row" spacing={2} mb={2}>
            <TextField
              size="small"
              placeholder="Search bugs..."
              fullWidth
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: { borderRadius: "10px" },
              }}
            />
            <Select
              size="small"
              value={statusFilter}
              onChange={(event) => setStatusFilter(String(event.target.value))}
              sx={{ minWidth: 140, borderRadius: "10px" }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="new">New</MenuItem>
              <MenuItem value="assigned">Assigned</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="blocked">Blocked</MenuItem>
            </Select>
            <Select
              size="small"
              value={priorityFilter}
              onChange={(event) =>
                setPriorityFilter(String(event.target.value))
              }
              sx={{ minWidth: 140, borderRadius: "10px" }}
            >
              <MenuItem value="all">All Priority</MenuItem>
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
            </Select>
          </Stack>

          <Divider />

          <Box
            sx={{
              maxHeight: 500,
              overflowY: "auto",
            }}
          >
            <Stack spacing={2} mt={2}>
              {pagedAssignments.length === 0 ? (
                <Box
                  p={3}
                  borderRadius={4}
                  bgcolor="#f3f6f9"
                  textAlign="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    No bug assignments found.
                  </Typography>
                </Box>
              ) : (
                pagedAssignments.map((bug, index) => {
                  const priorityColor =
                    {
                      urgent: "#f44336",
                      high: "#ff9500",
                      medium: "#378ADD",
                      low: "rgba(148, 163, 184, 1)",
                    }[bug.priority] ?? "rgba(148, 163, 184, 1)";

                  const overdue = isOverdue(bug.bug);

                  return (
                    <Box
                      key={index}
                      onClick={() => {
                        setSelectedBug(bug.bug);
                        setIsRecent(false);
                      }}
                      sx={{
                        borderRadius: "12px",
                        border: "1px solid rgba(226, 232, 240, 1)",
                        borderLeft: `3px solid ${priorityColor}`,
                        bgcolor: colors.primary[900],
                        p: 2,
                        cursor: "pointer",
                        transition: "border-color 0.15s ease",
                        "&:hover": { borderColor: "rgba(160, 174, 192, 1)" },
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        spacing={2}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {bug.bug.id}
                          </Typography>
                          <Typography fontWeight={600} noWrap>
                            {bug.bug.description}
                          </Typography>
                        </Box>
                        {(bug.bug.impact === "blocked" ||
                          bug.bug.impact === "slowed") && (
                          <WarningAmberIcon
                            sx={{
                              color:
                                bug.bug.impact === "blocked"
                                  ? "#f44336"
                                  : "#ff9500",
                              height: 20,
                              width: 20,
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </Stack>

                      {/* Footer: single dense meta row */}
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        flexWrap="wrap"
                        rowGap={1}
                        mt={1.5}
                        divider={
                          <Box
                            sx={{
                              width: "1px",
                              height: "14px",
                              bgcolor: "rgba(226, 232, 240, 1)",
                            }}
                          />
                        }
                      >
                        <Chip
                          label={bug.bug.module_name}
                          size="small"
                          sx={{
                            color: "#fff",
                            backgroundColor: "#A2845E",
                            height: 22,
                          }}
                        />

                        <Stack
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                        >
                          <Avatar
                            src={bug.assigned_to?.profile_picture ?? undefined}
                            sx={{ width: 20, height: 20, fontSize: 11 }}
                          >
                            {bug.assigned_to?.full_name?.charAt(0) ?? "?"}
                          </Avatar>
                          <Typography variant="caption" color="text.secondary">
                            {bug.assigned_to?.full_name || "Unassigned"}
                          </Typography>
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          <AccessTimeIcon
                            sx={{
                              height: 14,
                              width: 14,
                              color: overdue
                                ? "#f44336"
                                : "rgba(148, 163, 184, 1)",
                            }}
                          />
                          <Typography
                            variant="caption"
                            color={overdue ? "#f44336" : "text.secondary"}
                            fontWeight={overdue ? 600 : 400}
                          >
                            {bug.bug.due_date
                              ? formatDateTime(bug.bug.due_date)
                              : "No due date"}
                          </Typography>
                        </Stack>

                        <Chip
                          label={status_choices[bug.bug.status]}
                          size="small"
                          sx={{
                            color: "rgba(255, 149, 0, 1)",
                            backgroundColor: "rgba(255, 149, 0, 0.05)",
                            border: "1px solid rgba(255, 149, 0, 1)",
                            height: 22,
                          }}
                        />

                        {bug.bug.image && (
                          <PhotoLibraryIcon
                            sx={{
                              height: 18,
                              width: 18,
                              color: "rgba(165, 165, 165, 1)",
                            }}
                          />
                        )}
                      </Stack>
                    </Box>
                  );
                })
              )}
            </Stack>
          </Box>

          <Box
            mt={2}
            display="flex"
            justifyContent="flex-end"
            alignItems="flex-end"
            sx={{}}
          >
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mr: 5 }}
            >
              <Select
                size="small"
                value={rowsPerPage}
                onChange={(event) => setRowsPerPage(Number(event.target.value))}
                sx={{ minWidth: 120, borderRadius: "10px" }}
              >
                <MenuItem value={10}>10 / page</MenuItem>
                <MenuItem value={25}>25 / page</MenuItem>
                <MenuItem value={50}>50 / page</MenuItem>
              </Select>
            </Stack>

            <Box
              mt={2}
              display="flex"
              justifyContent="center"
              alignItems="center"
              gap={3}
            >
              <IconButton
                size="small"
                disabled={currentPage === 1}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                sx={{
                  border: "1px solid rgba(226, 232, 240, 1)",
                  borderRadius: "8px",
                }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>

              <Box
                sx={{
                  backgroundColor: "#724E41",
                  px: 2,
                  py: 0.5,
                  borderRadius: "8px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 32,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "#fff",
                    fontWeight: 600,
                  }}
                >
                  {currentPage}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                of
              </Typography>

              <Typography>{totalPages}</Typography>

              <IconButton
                size="small"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setPage((prev) => Math.min(prev + 1, totalPages))
                }
                sx={{
                  border: "1px solid rgba(226, 232, 240, 1)",
                  borderRadius: "8px",
                }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* =====================
         RIGHT DRAWER
      ===================== */}
      <Drawer
        anchor="right"
        open={Boolean(selectedBug)}
        onClose={() => setSelectedBug(null)}
        PaperProps={{ sx: { width: 420, p: 2 } }}
      >
        {selectedBug && (
          <>
            <Stack direction="row" justifyContent="space-between">
              <Stack spacing={2} direction={"row"}>
                <Typography
                  variant="caption"
                  color="rgba(130, 130, 130, 1)"
                  sx={{ fontSize: "14px" }}
                >
                  {selectedBug.id}
                </Typography>

                <Stack direction={"row"} spacing={0.5}>
                  <WarningIcon
                    sx={{
                      height: 20,
                      width: 20,
                      color: "rgba(255, 59, 48, 1)",
                    }}
                  />
                  <Typography sx={{ color: "rgba(255, 59, 48, 1)" }}>
                    {impact_choices[selectedBug.impact]}
                  </Typography>
                </Stack>
              </Stack>
              <IconButton onClick={() => setSelectedBug(null)}>
                <CloseIcon />
              </IconButton>
            </Stack>

            <Typography fontWeight={600} mt={2}>
              {selectedBug.description}
            </Typography>

            <Stack my={2} spacing={5} direction="row">
              <Typography variant="subtitle2" color="text.secondary">
                STATUS
              </Typography>

              <Typography color="text.secondary">
                {status_choices[selectedBug.status]}
              </Typography>
            </Stack>

            <Divider />

            {hasPermission(PERMISSIONS.BUG_CHANGE) && (
              <AssignmentPanel
                bug={selectedBug}
                assigning={assigning}
                existingAssigneeId={
                  assignments.find((a) => a.bug.id === selectedBug.id)
                    ?.assigned_to?.id ?? null
                }
                onAssign={handleAssign}
              />
            )}

            <Typography>AUTO CAPTURED CONTEXT</Typography>

            <Stack spacing={3} mt={1}>
              {/* Row 1 */}
              <Stack direction="row" spacing={4}>
                <InfoItem
                  icon={<PhotoLibraryIcon sx={{ color: labelColor }} />}
                  label="Page"
                  value={selectedBug.page}
                />
                <InfoItem
                  icon={<ViewModuleIcon sx={{ color: labelColor }} />}
                  label="Module"
                  value={selectedBug.module_name}
                />
              </Stack>

              {/* Row 2 */}
              <Stack direction="row" spacing={4}>
                <InfoItem
                  icon={<PersonOutlineIcon sx={{ color: labelColor }} />}
                  label="Reporter Role"
                  value={selectedBug.reporter_role}
                />
                <InfoItem
                  icon={<PublicIcon sx={{ color: labelColor }} />}
                  label="Browser"
                  value={selectedBug.browser}
                />
              </Stack>

              {/* Row 3 */}
              <Stack direction="row" spacing={4}>
                <InfoItem
                  icon={<DevicesIcon sx={{ color: labelColor }} />}
                  label="Device"
                  value={selectedBug.device}
                />
                <InfoItem
                  icon={<AspectRatioIcon sx={{ color: labelColor }} />}
                  label="Screen Resolution"
                  value={selectedBug.screen}
                />
              </Stack>

              {/* Row 4 */}
              <Stack direction="row" spacing={4}>
                <InfoItem
                  icon={<InfoOutlinedIcon sx={{ color: labelColor }} />}
                  label="App Version"
                  value={selectedBug.version}
                />
                {/* Empty column keeps alignment */}
                <Box flex={1} />
              </Stack>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography>USER REPORT</Typography>
            <Stack spacing={2} mt={1}>
              <Stack spacing={1}>
                <Typography color="rgba(148, 163, 184, 1)">
                  Issue Type:
                </Typography>
                <Typography>{bugtype_choices[selectedBug.bugtype]}</Typography>
              </Stack>

              <Stack spacing={1}>
                <Typography color="rgba(148, 163, 184, 1)">
                  What they were trying to do:
                </Typography>
                <Typography>{selectedBug.description}</Typography>
              </Stack>

              <Stack spacing={1}>
                <Typography color="rgba(148, 163, 184, 1)">
                  What went wrong:
                </Typography>
                <Typography>{selectedBug.problem}</Typography>
              </Stack>

              <Stack direction={"row"} spacing={5}>
                <Stack spacing={0.5}>
                  <Typography color="rgba(148, 163, 184, 1)">
                    Reproducibility:
                  </Typography>
                  <Typography>
                    {occurance_choices[selectedBug.occurence]}
                  </Typography>
                </Stack>
                <Stack spacing={0.5}>
                  <Typography color="rgba(148, 163, 184, 1)">
                    Impact:
                  </Typography>
                  <Typography>{impact_choices[selectedBug.impact]}</Typography>
                </Stack>
              </Stack>
            </Stack>

            <Divider sx={{ my: 2 }} />

            <Typography>VISUAL EVIDENCE</Typography>

            <Button
              variant="outlined"
              size="medium"
              disabled={!selectedBug.image}
              onClick={() => setIsImageOpen(true)}
              sx={{
                backgroundColor: "rgba(240, 244, 248, 1)",
                border: "1px rgba(227, 227, 227, 1) ",
                borderRadius: "26px",
                gap: 1.5,
                color: "#434546",
                py: 2,
                alignSelf: "flex-start",
                px: 4,
                mt: 2,
              }}
            >
              <Image
                sx={{
                  height: 20,
                  width: 20,
                }}
              />

              <Typography>View Screenshot</Typography>
            </Button>

            <Divider sx={{ my: 2 }} />

            <Stack spacing={1}>
              <Typography>TIMELINE</Typography>

              <Typography color="rgba(148, 163, 184, 1)">
                Reported {formatDateTime(selectedBug.created_at)}
              </Typography>
            </Stack>

            <Drawer
              anchor="bottom"
              open={isImageOpen}
              onClose={() => setIsImageOpen(false)}
              PaperProps={{
                sx: { p: 2, borderTopLeftRadius: 16, borderTopRightRadius: 16 },
              }}
            >
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography fontWeight={600}>Screenshot</Typography>
                  <IconButton onClick={() => setIsImageOpen(false)}>
                    <CloseIcon />
                  </IconButton>
                </Stack>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {selectedBug.image ? (
                    <Box
                      component="img"
                      src={
                        typeof selectedBug.image === "string"
                          ? selectedBug.image
                          : URL.createObjectURL(selectedBug.image)
                      }
                      alt="Bug screenshot"
                      sx={{
                        maxWidth: "100%",
                        maxHeight: "70vh",
                        borderRadius: 2,
                      }}
                    />
                  ) : (
                    <Typography color="text.secondary">
                      No screenshot available.
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Drawer>
          </>
        )}
      </Drawer>
    </Box>
  );
}
